/**
 * Pure, deterministic game engine. The host (web UI / server replay) owns the
 * clock and calls advance(now) / submitAnswer(now). Given the same seed,
 * start/reference rating and event list, the resulting state is identical —
 * that is what server-side validation relies on.
 *
 * Spec: docs/GAME_DESIGN.md § Core loop.
 *
 * Clock contract:
 * - The engine clock starts at `createGame({ now })` (default 0). Server replay
 *   always starts at 0, so hosts of validated runs must start at 0 and pass
 *   engine time (paused time excluded) — never wall-clock timestamps.
 * - Time never runs backwards: advance() ignores `now < state.now`, and
 *   submitAnswer()/endGame() treat an earlier `now` as `state.now` (the logged
 *   event carries the effective time, so replays match).
 * - At game over the clock freezes at the moment the run ended (`phaseEndsAt`
 *   holds that moment too); later advance() calls return the state unchanged.
 * - In phase `falling`, `phaseEndsAt` mirrors `active.lockAt`.
 *
 * States are never mutated: every call returns a new object (sharing unchanged
 * parts) or the input itself when nothing happened.
 */
import { clampRating, levelForRating } from '../adaptive/rating.ts';
import { parseAnswer } from '../algebra/answer.ts';
import { DEFAULT_GAME_CONFIG, RULES_VERSION } from '../config.ts';
import { eq } from '../math/rational.ts';
import type { GameConfig, GameEvent, GameState, InputVia } from '../types.ts';
import { drawProblem, finishPhase, nextTransitionAt, resolveActive, spawnNext } from './transitions.ts';

export { blockRow, landingRow, timeLeftFraction } from './geometry.ts';
export { summarize } from './summary.ts';

export interface CreateGameOptions {
  seed: number;
  /** Rating the run starts at (server-issued warm start). Default 0 → level 1. */
  startRating?: number;
  /** Player's established ability for anti-farming. Default = startRating. */
  referenceRating?: number;
  /** Engine clock at start. Default 0. */
  now?: number;
  config?: Partial<GameConfig>;
}

/**
 * Upper bound on automatic transitions in one advance() call. Every lock adds a
 * brick, so a run can only time out ~wellRows times in a row before game over;
 * hitting this bound means the engine is broken, not that the gap was long.
 */
const MAX_TRANSITIONS_PER_ADVANCE = 10_000;

/** Merge and sanity-check a config override. Throws RangeError on values the engine can't run with. */
export function resolveGameConfig(override: Partial<GameConfig> = {}): GameConfig {
  const config: GameConfig = { ...DEFAULT_GAME_CONFIG, ...override };
  const wholeAtLeast = (name: 'wellRows' | 'stepsPerRow' | 'checkpointSize', min: number) => {
    const v = config[name];
    if (!Number.isInteger(v) || v < min) throw new RangeError(`GameConfig.${name} must be an integer ≥ ${min}, got ${v}`);
  };
  wholeAtLeast('wellRows', 2);
  wholeAtLeast('stepsPerRow', 1);
  wholeAtLeast('checkpointSize', 1);
  for (const name of ['correctResolveMs', 'wrongResolveMs', 'timeoutResolveMs', 'levelBannerMs'] as const) {
    const v = config[name];
    if (!Number.isFinite(v) || v < 0) throw new RangeError(`GameConfig.${name} must be a finite duration ≥ 0, got ${v}`);
  }
  if (!Number.isFinite(config.paceScale) || config.paceScale <= 0) {
    throw new RangeError(`GameConfig.paceScale must be a finite number > 0, got ${config.paceScale}`);
  }
  return config;
}

/**
 * Start a run: problem #1 spawns immediately as the falling block at `now`, and
 * problem #2 is drawn for the preview. The level comes from the start rating.
 */
export function createGame(opts: CreateGameOptions): GameState {
  if (!Number.isFinite(opts.seed)) throw new RangeError(`createGame: seed must be finite, got ${opts.seed}`);
  const now = opts.now ?? 0;
  if (!Number.isFinite(now)) throw new RangeError(`createGame: now must be finite, got ${now}`);
  const config = resolveGameConfig(opts.config);
  const startRating = clampRating(opts.startRating ?? 0);
  const referenceRating = clampRating(opts.referenceRating ?? startRating);
  const level = levelForRating(startRating);
  const first = drawProblem(opts.seed, level, 1);

  const beforeSpawn: GameState = {
    rulesVersion: RULES_VERSION,
    config,
    seed: opts.seed,
    rngState: first.rngState,
    now,
    startedAt: now,
    phase: 'falling',
    phaseEndsAt: now,
    bricks: 0,
    active: null,
    next: first.problem,
    nextSeq: 2,
    level,
    levelStart: level,
    maxLevel: level,
    rating: startRating,
    startRating,
    peakRating: startRating,
    referenceRating,
    window: [],
    history: [],
    checkpoints: [],
    levelAnswered: 0,
    streak: 0,
    bestStreak: 0,
    layersBroken: 0,
    raw: 0,
    rated: 0,
    lastResolution: null,
    pendingLevelChange: null,
    probation: null,
    events: [],
  };
  return spawnNext(beforeSpawn, now);
}

/**
 * Advance the clock to `now`: locks/timeouts, phase transitions and spawns, in
 * time order — including several lock/spawn cycles after a long gap. Gravity is
 * implicit (see blockRow). Idempotent for equal `now`; ignores `now < state.now`.
 */
export function advance(state: GameState, now: number): GameState {
  if (state.phase === 'gameOver' || !(now > state.now)) return state;
  let s = state;
  for (let i = 0; i < MAX_TRANSITIONS_PER_ADVANCE; i++) {
    if (s.phase === 'gameOver') return s;
    const due = nextTransitionAt(s);
    if (due > now) return { ...s, now };
    s = s.phase === 'falling' ? resolveActive(s, 'timeout', due, null, null) : finishPhase(s);
  }
  throw new Error(`advance: more than ${MAX_TRANSITIONS_PER_ADVANCE} transitions between ${state.now} and ${now}`);
}

/**
 * Submit an answer for the active block. `value` is the raw text (MC choices are
 * submitted as their formatted value, e.g. "-3/4"). Ignored — the input state is
 * returned unchanged — unless a block is falling and has not locked by `now`.
 * Unparseable input counts as a wrong answer (given = null).
 */
export function submitAnswer(state: GameState, input: { value: string; via: InputVia }, now: number): GameState {
  const at = Math.max(now, state.now);
  if (!Number.isFinite(at)) return state;
  const s = advance(state, at);
  const block = s.active;
  if (s.phase !== 'falling' || !block || at >= block.lockAt) return state;

  const given = parseAnswer(input.value);
  const outcome = given !== null && eq(given, block.problem.answer) ? 'correct' : 'wrong';
  const event: GameEvent = { type: 'answer', at, problemId: block.problem.id, value: input.value, via: input.via };
  return resolveActive({ ...s, events: [...s.events, event] }, outcome, at, given, input.via);
}

/**
 * Player quits: game over now (logs an `end` event). If the run already ended
 * on its own by `now`, returns that finished state without logging anything.
 */
export function endGame(state: GameState, now: number): GameState {
  const at = Math.max(now, state.now);
  if (!Number.isFinite(at)) return state;
  const s = advance(state, at);
  if (s.phase === 'gameOver') return s;
  const event: GameEvent = { type: 'end', at };
  return { ...s, now: at, phase: 'gameOver', phaseEndsAt: at, active: null, events: [...s.events, event] };
}
